import {atomWithoutDefault} from '@raison/toolbox';
import {useAtomValue} from 'jotai';
import {HttpClient, createOutput} from './HttpClient';

export namespace UploadClient {
    export interface Init extends Pick<HttpClient.Init, 'headers' | 'method' | 'signal'> {
        body: FormData;
        onProgress?: (percent: number) => void;
    }

    export const base = baseUploadClient;
}

export type UploadClient = typeof baseUploadClient;

async function baseUploadClient(
    url: string,
    {
        body,
        method = 'POST',
        headers,
        signal,
        onProgress,
        //
    }: UploadClient.Init
): HttpClient.Output {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        if (signal) {
            if (signal.aborted) {
                reject(signal.reason);
                return;
            }
            signal.addEventListener('abort', () => {
                xhr.abort();
            });
            xhr.addEventListener('abort', () => {
                reject(signal.reason);
            });
        }

        xhr.open(method, url, true);

        headers = new Headers(headers);
        for (const key of headers.keys()) {
            xhr.setRequestHeader(key, headers.get(key)!);
        }

        if (onProgress) {
            xhr.upload.addEventListener('progress', event => {
                if (event.lengthComputable) {
                    const percent = (event.loaded / event.total) * 100;
                    onProgress(percent);
                }
            });
        }

        xhr.addEventListener('load', () => {
            let responseBody;
            if (xhr.responseType === 'arraybuffer' || xhr.responseType === 'blob') {
                responseBody = xhr.response;
            } else {
                responseBody = xhr.responseText;
            }

            const responseHeadersInit = xhr
                .getAllResponseHeaders()
                .trim()
                .split('\r\n')
                .map((line): [string, string] => {
                    const headerKey = line.slice(0, line.indexOf(':'));
                    const headerVal = xhr.getResponseHeader(headerKey)!;
                    return [headerKey, headerVal];
                });

            const response = new Response(responseBody, {
                headers: new Headers(responseHeadersInit),
                status: xhr.status,
                statusText: xhr.statusText,
            });

            const request = new Request(url, {method, headers});

            resolve(createOutput(response, request));
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Upload failed due to a network error'));
        });

        xhr.send(body);
    });
}

export const UploadClient$ = atomWithoutDefault<UploadClient>('UploadClient$');
export function useUploadClient() {
    return useAtomValue(UploadClient$);
}
